import { TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";

const MIN_MINUTES = 0;
const WHEEL_STEP = 1;

const normalizeMinutes = (minutes: number): number | null =>
  minutes <= MIN_MINUTES ? null : minutes;

const toInputValue = (minutes: number | null): string =>
  minutes === null ? "" : String(minutes);

interface MinMinutesFilterProps {
  value: number | null;
  onChange: (value: number | null) => void;
}

export const MinMinutesFilter = ({
  value,
  onChange,
}: MinMinutesFilterProps) => {
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const valueRef = useRef(value);
  const draftRef = useRef(draft);
  const onChangeRef = useRef(onChange);
  valueRef.current = value;
  draftRef.current = draft;
  onChangeRef.current = onChange;

  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      const currentValue = valueRef.current;
      const isIncrement = event.deltaY < 0;
      if (currentValue === null && !isIncrement) return;

      const nextValue = normalizeMinutes(
        (currentValue ?? 0) + (isIncrement ? WHEEL_STEP : -WHEEL_STEP),
      );
      if (draftRef.current !== null) setDraft(toInputValue(nextValue));
      onChangeRef.current(nextValue);
    };

    field.addEventListener("wheel", handleWheel, { passive: false });
    return () => field.removeEventListener("wheel", handleWheel);
  }, []);

  return (
    <TextField
      ref={fieldRef}
      label="Min minutes"
      type="number"
      variant="outlined"
      size="small"
      fullWidth
      value={draft ?? toInputValue(value)}
      onFocus={() => setDraft(toInputValue(value))}
      onBlur={() => setDraft(null)}
      onChange={(event) => {
        const rawValue = event.target.value;
        setDraft(rawValue);

        if (rawValue === "") {
          onChange(null);
          return;
        }

        const parsedValue = Number.parseInt(rawValue, 10);
        if (Number.isNaN(parsedValue)) return;

        onChange(normalizeMinutes(parsedValue));
      }}
      slotProps={{
        htmlInput: {
          min: MIN_MINUTES,
          step: 1,
          inputMode: "numeric",
        },
      }}
    />
  );
};
